import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format as formatDate } from 'date-fns';
import { clickPerformanceApi } from '../../services/api';

const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)}%`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>{label}</Typography>
      {payload.map((entry) => entry.value != null && (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {fmtPct(entry.value)}
        </Typography>
      ))}
      {d && (
        <Typography variant="caption" color="text.secondary">
          {d.clicks?.toLocaleString()} clicks · {d.approved_orders?.toLocaleString()} orders
        </Typography>
      )}
    </Paper>
  );
};

const ClickPerformanceTrends = ({ fromDate, toDate, retailerId }) => {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [direction, setDirection] = useState('both');

  // Reset direction to 'both' when switching back to network view
  useEffect(() => {
    if (!retailerId) setDirection('both');
  }, [retailerId]);

  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await clickPerformanceApi.getTrends({
        from_date: fromDate,
        to_date: toDate,
        retailer_id: retailerId,
        direction,
      });
      if (result.success) {
        setTrends(result.data || []);
      } else {
        setError(result.error || 'Failed to load trends');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, retailerId, direction]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  // 7-day rolling conversion rate: sum(orders[i-6..i]) / sum(clicks[i-6..i])
  const chartData = trends.map((t, i, arr) => {
    const window = arr.slice(Math.max(0, i - 6), i + 1);
    const totalClicks = window.reduce((s, d) => s + d.clicks, 0);
    const totalOrders = window.reduce((s, d) => s + d.approved_orders, 0);
    return {
      ...t,
      label: formatDate(new Date(t.date), 'MMM d'),
      rolling7: totalClicks >= 5 ? totalOrders / totalClicks : null,
    };
  });

  const xInterval = chartData.length > 14 ? Math.floor(chartData.length / 10) : 0;

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }} elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Conversion rate trend <Typography component="span" variant="caption" color="text.secondary">(7-day rolling)</Typography></Typography>
        {retailerId && (
          <ToggleButtonGroup
            value={direction}
            exclusive
            onChange={(_, v) => v && setDirection(v)}
            size="small"
          >
            <ToggleButton value="both">Both</ToggleButton>
            <ToggleButton value="sent">Sent</ToggleButton>
            <ToggleButton value="received">Received</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && trends.length === 0 && (
        <Typography color="text.secondary">No snapshot data for this period. Run the backfill script to populate history.</Typography>
      )}
      {!loading && !error && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" interval={xInterval} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={fmtPct} tick={{ fontSize: 12 }} width={48} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="conversion_rate"
              name="Daily"
              stroke="#1976d2"
              dot={false}
              strokeWidth={1}
              strokeOpacity={0.3}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="rolling7"
              name="7-day avg"
              stroke="#1976d2"
              dot={false}
              strokeWidth={2.5}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export default ClickPerformanceTrends;
